import React, { memo, useState } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { Link } from 'react-router-dom'

import Text from '../../../../ui/Text'
import { WidgetLayout } from '../../../../lib/widget-layout'
import { ChargersPagination } from '../../../../lib/chargers-pagination'
import { Table, TableHeaderCell, TableHeader, TableRow, TableBody, TableBodyCell } from '../../../../lib/table'
import { STableLink } from '../../../../lib/table/Table.styles'
import {
    LoadBalancerGroupGetAllPagedApiArg,
    LoadBalancerGroup,
    PagedResultOfLoadBalancerGroup,
} from '../../../redux/api/web-api'
import Input from '../../../components/Input'
import { Button } from '../../../components/Button'
import useText from '../../../hooks/useText'

export const SLink = styled.a`
    &,
    &:not(:hover) {
        color: ${({ theme }) => theme.font.darkestColor};
        text-decoration: underline;
        font-size: 0.75rem;
    }

    &:hover {
        color: ${({ theme }) => theme.font.successColor};
    }

    &:active {
        color: ${({ theme }) => theme.font.midDarkColor};
    }
`

export const SButtonGroup = styled.span`
    display: flex;
    gap: 9px;
    margin-top: 10px;
    justify-content: flex-end;
`

export const SInputGroup = styled.span`
    display: flex;
    gap: 9px;
`

export const SSearchFormWrapper = styled.div`
    width: 100%;
    padding-bottom: 10px;
`

export type LoadbalancerGroupsWidgetProps = {
    loadBalancerGroups?: PagedResultOfLoadBalancerGroup
    loadingGroups?: boolean
    className?: ClassName
    style?: React.CSSProperties
}

export enum SortParameter {
    NAME = 'name',
    ACTIVE = 'active',
    ID = 'id',
    LAST_HEARTBEAT = 'lastHeartbeat',
    WIFI_SIGNAL_STRENGTH = 'wifiSignalStrength',
    PARTNER_DESCRIPTION = 'partnerDescription',
}

const defaultFilters = {
    id: '',
    name: '',
    page: 0,
    noOfItems: 20,
}

export const LoadbalancerGroupsWidget = memo(function LoadbalancerGroupsWidget({
    className,
    style,
    loadBalancerGroups,
    loadingGroups,
}: LoadbalancerGroupsWidgetProps): React.ReactElement {
    const text = useText()
    const [filters, setFilters] = useState<LoadBalancerGroupGetAllPagedApiArg>(defaultFilters)
    const [currentPage, setCurrentPage] = useState<number>(0)

    const pageSize = filters?.noOfItems
    const offset = pageSize && currentPage * pageSize

    const { register, handleSubmit, reset } = useForm<LoadBalancerGroupGetAllPagedApiArg>({
        defaultValues: filters,
    })
    const filterSubmit = (filters: Partial<LoadBalancerGroupGetAllPagedApiArg>) => {
        setFilters(filters)
        setCurrentPage(0)
    }

    const handlePageClick = (pageNumber: number) => {
        setFilters({ ...filters, page: pageNumber })
    }

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblLoadbalancerGroups')}
            data-testid="lb-table"
        >
            <form name="form" role="form" noValidate onSubmit={handleSubmit(filterSubmit)}>
                <SInputGroup>
                    <Input id="lb-id" labelText={text('lblId')} {...register('id')} data-testid="input-lb-id"></Input>
                    <Input
                        id="lb-name"
                        labelText={text('lblName')}
                        {...register('name')}
                        data-testid="input-lb-name"
                    ></Input>
                </SInputGroup>
                <SButtonGroup>
                    <Button
                        label={text('btnSearch')}
                        size="sm"
                        data-testid="btn-search"
                        loading={loadingGroups}
                        type="submit"
                    ></Button>
                    <Button
                        label={text('btnClear')}
                        size="sm"
                        data-testid="btn-clear"
                        onClick={() => reset()}
                        type="submit"
                    ></Button>
                </SButtonGroup>
            </form>
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHeaderCell data-testid="table-th-id">
                            <Text code="lblId" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-name">
                            <Text code="lblName" />
                        </TableHeaderCell>
                        <TableHeaderCell data-testid="table-th-name">
                            <Text code="lblStatus" />
                        </TableHeaderCell>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {!loadingGroups &&
                        loadBalancerGroups?.items?.slice(offset, offset + pageSize)?.map((group: LoadBalancerGroup) => (
                            <TableRow key={group?.id}>
                                <TableBodyCell data-testid="table-tb-id">
                                    <STableLink as={Link} to={`/loadbalancer/${group?.id}`}>
                                        {group?.id}
                                    </STableLink>
                                </TableBodyCell>
                                <TableBodyCell data-testid="table-tb-name">{group?.name}</TableBodyCell>
                                <TableBodyCell data-testid="table-tb-name">
                                    {text(group?.active ? 'lblActive' : 'lblInactiveStatus')}
                                </TableBodyCell>
                            </TableRow>
                        ))}
                </TableBody>
            </Table>
            {loadingGroups && <div className="shimmer-container shimmer-small" data-testid="lb-loading" />}
            {loadBalancerGroups?.items && loadBalancerGroups?.items?.length > 0 && (
                <ChargersPagination
                    setPageIndex={handlePageClick}
                    totalCount={loadBalancerGroups?.totalCount}
                    pageSize={pageSize ?? 0}
                    pageIndex={filters?.page ?? 0}
                    displayedChargers={pageSize ?? 0}
                />
            )}
        </WidgetLayout>
    )
})
